import { Bar, Line } from "react-chartjs-2";
import type { ScriptableContext, TooltipItem } from "chart.js";
import { BodyMetricType } from "../../../../enums";
import { dateUtility } from "../../../../infrastructure/utilities/dateUtility";
import {
  CHART_GRID_COLOR,
  CHART_TEXT_COLOR,
} from "../../../../../components/portal/myWorkouts/statistics/charts/registerCharts";
import { IBodyMetricEntry } from "../../models/bodyMetricModels";
import { BODY_METRIC_TYPES } from "../../models/bodyMetricTypes";
import { BODY_METRIC_CHART_COLORS, TRANSPARENT } from "../../models/bodyMetricChartColors";
import {
  formatBodyMetricChange,
  formatBodyMetricValue,
} from "../../utils/bodyMetricsUtility";

const shortDay = (date: string) => dateUtility.formatDate(date, "dd.MM");

const axes = {
  x: { grid: { display: false }, ticks: { color: CHART_TEXT_COLOR, maxRotation: 0, autoSkipPadding: 12 } },
  y: { grid: { color: CHART_GRID_COLOR }, ticks: { color: CHART_TEXT_COLOR, maxTicksLimit: 5 } },
};

// A tap anywhere picks the nearest reading - the lesson of the muscle radar (Trello #1112).
const interaction = { mode: "nearest" as const, intersect: false, axis: "x" as const };

interface IBodyMetricTrendChartProps {
  type: BodyMetricType;
  readings: IBodyMetricEntry[];
}

/** The value over time, with a soft fill under the line. */
export const BodyMetricTrendChart = ({ type, readings }: IBodyMetricTrendChartProps) => {
  const colors = BODY_METRIC_CHART_COLORS[BODY_METRIC_TYPES[type].group];

  const data = {
    labels: readings.map((e) => shortDay(e.measuredOn)),
    datasets: [
      {
        data: readings.map((e) => e.value),
        borderColor: colors.line,
        borderWidth: 2.5,
        tension: 0.35,
        fill: true,
        backgroundColor: ({ chart }: ScriptableContext<"line">) => {
          const { ctx, chartArea } = chart;
          if (!chartArea) return colors.fill;
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, colors.fill);
          gradient.addColorStop(1, TRANSPARENT);
          return gradient;
        },
        pointBackgroundColor: colors.line,
        pointRadius: readings.length > 30 ? 0 : 3.5,
        pointHoverRadius: 6,
        pointHitRadius: 20,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    locale: "bg-BG",
    interaction,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<"line">[]) =>
            dateUtility.formatDate(readings[items[0].dataIndex].measuredOn),
          label: (item: TooltipItem<"line">) => formatBodyMetricValue(type, item.parsed.y),
        },
      },
    },
    scales: { ...axes, y: { ...axes.y, grace: "10%" } },
  };

  return <Line data={data} options={options} />;
};

interface IBodyMetricChangesChartProps {
  type: BodyMetricType;
  changes: { entry: IBodyMetricEntry; change: number | null }[];
}

/** How much every reading moved from the one before it: up and down bars around zero. */
export const BodyMetricChangesChart = ({ type, changes }: IBodyMetricChangesChartProps) => {
  const colors = BODY_METRIC_CHART_COLORS[BODY_METRIC_TYPES[type].group];
  const values = changes.map((c) => c.change ?? 0);

  const data = {
    labels: changes.map((c) => shortDay(c.entry.measuredOn)),
    datasets: [
      {
        data: values,
        backgroundColor: values.map((v) => (v >= 0 ? colors.line : colors.soft)),
        borderColor: colors.line,
        borderWidth: values.map((v) => (v >= 0 ? 0 : 1.5)),
        borderRadius: 6,
        maxBarThickness: 28,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    locale: "bg-BG",
    interaction,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: TooltipItem<"bar">[]) =>
            dateUtility.formatDate(changes[items[0].dataIndex].entry.measuredOn),
          label: (item: TooltipItem<"bar">) => formatBodyMetricChange(type, item.parsed.y),
        },
      },
    },
    scales: axes,
  };

  return <Bar data={data} options={options} />;
};
